// v4 (spec-v4-structure-and-stage.md sections 2.1-2.3, 3.1, 5.1; ticket V4-H2): the web's client for channels and
// collections (V4-H1's routes), the "where am I" state (the current channel, remembered in localStorage) and the pure
// helpers the tree screens share: kind words, the "from the series" chips, setting rows, the rolled-up next step,
// breadcrumbs and where a video can move. The web doesn't import core, so the kind table is copied here; keep it in
// step with product/core/src/collections.ts COLLECTION_KIND_OF.
//
// The source is swappable (installCollections): the mock (src/mock/collections.ts) replaces it, like the stage does.
import { useEffect, useState } from "react";
import { call } from "./api";
import { ASPECT_WORD, isFormatId, type FormatId, type FormatInfo } from "./formats";
import { normNextStep, type NextStepData, type NextStepId } from "./nextstep";
import { channelInPath, useLocation } from "./router";
import { bump, toast, useRefresh, useStore } from "./store";
import type { Channel } from "./types";

// ------------------------------------------------------------------ kinds and groups (core collections.ts, copied)

export const COLLECTION_KINDS = ["series", "product", "song", "show", "event", "course"] as const;
export type CollectionKind = (typeof COLLECTION_KINDS)[number];
export const SETTINGS_GROUPS = ["story", "product", "song", "show", "faceless", "event", "course"] as const;
export type SettingsGroup = (typeof SETTINGS_GROUPS)[number];

/** core COLLECTION_KIND_OF: what collection a format's videos can belong to (null: always stand-alone). */
export const KIND_OF: Record<FormatId, { kind: CollectionKind; group: SettingsGroup } | null> = {
  cartoon: { kind: "series", group: "story" },
  "short-film": { kind: "series", group: "story" },
  explainer: { kind: "series", group: "story" },
  ad: { kind: "product", group: "product" },
  "music-video": { kind: "song", group: "song" },
  documentary: { kind: "show", group: "show" },
  podcast: { kind: "show", group: "show" },
  short: { kind: "series", group: "faceless" },
  slideshow: { kind: "event", group: "event" },
  "math-explainer": { kind: "course", group: "course" },
  kinetic: null,
  demo: null,
};

export const kindOf = (format: string | null | undefined) => (isFormatId(format) ? KIND_OF[format] : null);
/** The formats a collection of this group takes (its row in the table). */
export const formatsOfGroup = (group: SettingsGroup): FormatId[] => (Object.keys(KIND_OF) as FormatId[]).filter((f) => KIND_OF[f]?.group === group);
/** A video fits a collection when its format is in the collection's group (a cartoon series takes a short-film trailer). */
export const fitsGroup = (format: string | null | undefined, group: SettingsGroup) => kindOf(format)?.group === group;

/** "Series", "Product"... (the card's kind line). */
export const KIND_WORD: Record<CollectionKind, string> = { series: "Series", product: "Product", song: "Song", show: "Show", event: "Event", course: "Course" };
/** What a collection is called in a sentence ("the series", "the show"). */
const KIND_NOUN: Record<CollectionKind, string> = { series: "series", product: "product line", song: "song", show: "show", event: "event", course: "course" };
export const kindNoun = (kind: CollectionKind | null | undefined) => (kind ? KIND_NOUN[kind] : "collection");
/** The group's own word where it differs from the kind's (a faceless series of Shorts). */
export const groupWord = (kind: CollectionKind, group: SettingsGroup) => (group === "faceless" ? "Shorts series" : KIND_WORD[kind]);

/** The choices in "New collection": one per group, with the format it's started from. */
export const GROUP_OPTIONS: { group: SettingsGroup; kind: CollectionKind; format: FormatId; title: string; blurb: string }[] = [
  { group: "story", kind: "series", format: "cartoon", title: "Story series", blurb: "Cartoons, short films and explainers that share a cast, a look and theme music" },
  { group: "faceless", kind: "series", format: "short", title: "Shorts series", blurb: "Shorts with the same narrator, captions and hook" },
  { group: "product", kind: "product", format: "ad", title: "Product", blurb: "Ads for one product: its photos, logo, colours and approved claims" },
  { group: "show", kind: "show", format: "documentary", title: "Show", blurb: "Documentaries and podcasts with the same hosts, sources and intro" },
  { group: "song", kind: "song", format: "music-video", title: "Song", blurb: "Music videos for one song, with its lyrics and the artist's look" },
  { group: "event", kind: "event", format: "slideshow", title: "Event", blurb: "Slideshows from one pool of photos and music" },
  { group: "course", kind: "course", format: "math-explainer", title: "Course", blurb: "Maths lessons with the same board and narrator" },
];

// ------------------------------------------------------------------ shapes (V4-H1's responses, normalised)

export type SettingSource = "video" | "collection" | "channel" | "default";
export type Resolved = { value: unknown; from: SettingSource };
export type Needs = { review: number; sheets: number; olderLooks: number };

/** GET /channels: a channel with its counts. */
export type ChannelCard = Channel & { collectionCount: number; videoCount: number };

/** A collection on a card (tree, list, rail). */
export type CollectionCard = {
  id: string;
  channelId: string;
  kind: CollectionKind;
  group: SettingsGroup;
  formats: FormatId[];
  name: string;
  description: string;
  coverUrl: string | null;
  thumbUrl: string | null;
  videoCount: number;
  needs: Needs;
  /** the collection's own settings (only on the detail and after a PATCH) */
  settings: Record<string, unknown>;
  updatedAt: string | null;
};

/** A video on a card (tree, collection screen). */
export type VideoCard = {
  id: string;
  title: string;
  format: FormatId;
  formatName: string;
  status: string;
  channelId: string | null;
  collectionId: string | null;
  needsReview: number;
  /** shots made with an older approved look (V4-C3 fills it; 0 until then) */
  olderLooks: number;
  thumbUrl: string | null;
  previewUrl: string | null;
  updatedAt: string | null;
};

export type ChannelTree = { channel: ChannelCard; collections: CollectionCard[]; oneOffs: VideoCard[]; library: { count: number } };

export type SheetState = "none" | "making" | "draft" | "approved";
/** A cast member on a collection (V4-C1's CharacterView, the bits a card shows). */
export type CastMember = {
  id: string;
  slug: string;
  name: string;
  look: string;
  voiceName: string | null;
  state: SheetState;
  approvedN: number | null;
  latestN: number | null;
  thumbUrl: string | null;
  olderShots: number;
};
export type Place = { id: string; name: string; description: string; assetId: string | null };

export type CollectionDetail = {
  collection: CollectionCard;
  settings: Record<string, Resolved>;
  videos: VideoCard[];
  characters: CastMember[];
  locations: { locations: Place[]; props: Place[] };
  counts: { videos: number; characters: number } & Needs;
  nextStep: NextStepData | null;
};

/** A render-time value the collection changed since the video's snapshot ("The series has new theme music. Use it?"). */
export type PendingSetting = { key: string; from: SettingSource | null; text: string };
/** GET /projects/:id/settings. */
export type VideoSettings = { kind: CollectionKind | null; group: SettingsGroup | null; collectionId: string | null; settings: Record<string, Resolved>; pending: PendingSetting[] };

/** A clip that can be the channel's intro or outro (a video in the library marked with that role). */
export type Clip = { id: string; name: string; url: string | null; thumbUrl: string | null; role: string | null; durationS: number | null };

export type CollectionPatch = { name?: string; description?: string; settings?: Record<string, unknown | null> };

// ------------------------------------------------------------------ normalisers

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => !!v && typeof v === "object" && !Array.isArray(v);
const s = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));
const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && Number.isFinite(+v) ? +v : 0);
const sOrNull = (v: unknown) => (typeof v === "string" && v ? v : null);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const SOURCES: SettingSource[] = ["video", "collection", "channel", "default"];

const kindOr = (v: unknown): CollectionKind => ((COLLECTION_KINDS as readonly string[]).includes(s(v)) ? (s(v) as CollectionKind) : "series");
const groupOr = (v: unknown, kind: CollectionKind): SettingsGroup =>
  (SETTINGS_GROUPS as readonly string[]).includes(s(v)) ? (s(v) as SettingsGroup) : (GROUP_OPTIONS.find((o) => o.kind === kind)?.group ?? "story");

export function normNeeds(v: unknown): Needs {
  const o = isObj(v) ? v : {};
  return { review: n(o.review), sheets: n(o.sheets), olderLooks: n(o.olderLooks) };
}

export function normChannelCard(v: unknown): ChannelCard {
  const o = isObj(v) && isObj(v.channel) ? v.channel : isObj(v) ? v : {};
  const b = isObj(o.brand) ? o.brand : {};
  const c = isObj(b.colors) ? b.colors : {};
  return {
    id: s(o.id),
    name: s(o.name),
    // the brand stays loose here (screens/Channel.tsx reads the full brand through api.channels())
    brand: { colors: { bg: s(c.bg) || "#101522", fg: s(c.fg) || "#F4EFE6", accent: s(c.accent) || "#FFB23E" } },
    collectionCount: n(o.collectionCount),
    videoCount: n(o.videoCount),
  };
}

export function normCollectionCard(v: unknown): CollectionCard {
  const o = isObj(v) && isObj(v.collection) ? v.collection : isObj(v) ? v : {};
  const kind = kindOr(o.kind);
  const group = groupOr(o.group ?? o.settingsGroup ?? o.settings_group, kind);
  const formats = arr(o.formats).map(s).filter(isFormatId);
  return {
    id: s(o.id),
    channelId: s(o.channelId ?? o.channel_id),
    kind,
    group,
    formats: formats.length ? formats : formatsOfGroup(group),
    name: s(o.name) || "Untitled collection",
    description: s(o.description),
    coverUrl: sOrNull(o.coverUrl),
    thumbUrl: sOrNull(o.thumbUrl),
    videoCount: n(o.videoCount ?? (Array.isArray(o.videos) ? o.videos.length : 0)),
    needs: normNeeds(o.needs),
    settings: isObj(o.settings) ? o.settings : {},
    updatedAt: sOrNull(o.updatedAt ?? o.updated_at),
  };
}

export function normVideoCard(v: unknown): VideoCard {
  const o = isObj(v) && isObj(v.project) ? v.project : isObj(v) ? v : {};
  const format = isFormatId(o.format) ? o.format : "ad";
  return {
    id: s(o.id),
    title: s(o.title) || (format === "ad" ? "Untitled ad" : "Untitled video"),
    format,
    formatName: s(o.formatName),
    status: s(o.status) || "draft",
    channelId: sOrNull(o.channelId ?? o.channel_id),
    collectionId: sOrNull(o.collectionId ?? o.collection_id),
    needsReview: n(o.needsReview),
    olderLooks: n(o.olderLooks ?? (isObj(o.needs) ? o.needs.olderLooks : 0)),
    thumbUrl: sOrNull(o.thumbUrl),
    previewUrl: sOrNull(o.previewUrl),
    updatedAt: sOrNull(o.updatedAt ?? o.updated_at),
  };
}

export function normTree(v: unknown): ChannelTree {
  const o = isObj(v) ? v : {};
  return {
    channel: normChannelCard(o.channel),
    collections: arr(o.collections).map(normCollectionCard),
    oneOffs: arr(o.oneOffs).map(normVideoCard),
    library: { count: n(isObj(o.library) ? o.library.count : 0) },
  };
}

export function normResolved(v: unknown): Record<string, Resolved> {
  // `{values: {...}}` (core ResolvedSettings) or the values map itself
  const o = isObj(v) && isObj(v.values) ? v.values : isObj(v) ? v : {};
  const out: Record<string, Resolved> = {};
  for (const [k, r] of Object.entries(o)) if (isObj(r) && "value" in r) out[k] = { value: r.value, from: SOURCES.includes(r.from as SettingSource) ? (r.from as SettingSource) : "default" };
  return out;
}

const STATES: SheetState[] = ["none", "making", "draft", "approved"];
const mediaUrl = (v: unknown) => (isObj(v) ? sOrNull(v.url) : sOrNull(v));
/** A version's best small picture: the front panel, else the turnaround. */
const versionThumb = (ver: unknown) => {
  if (!isObj(ver)) return null;
  const panels = isObj(ver.panels) ? ver.panels : {};
  const sheets = isObj(ver.sheets) ? ver.sheets : {};
  return mediaUrl(panels.front) ?? mediaUrl(panels.three_quarter) ?? mediaUrl(sheets.turnaround) ?? null;
};

export function normCast(v: unknown): CastMember {
  const o = isObj(v) && isObj(v.character) ? v.character : isObj(v) ? v : {};
  const approved = isObj(o.approved) ? o.approved : null;
  const latest = isObj(o.latest) ? o.latest : null;
  return {
    id: s(o.id),
    slug: s(o.slug) || s(o.id),
    name: s(o.name) || "Someone",
    look: s(o.look),
    voiceName: sOrNull(o.voiceName),
    state: STATES.includes(o.state as SheetState) ? (o.state as SheetState) : approved ? "approved" : "none",
    approvedN: approved ? n(approved.n) || null : null,
    latestN: latest ? n(latest.n) || null : null,
    thumbUrl: sOrNull(o.thumbUrl) ?? versionThumb(o.state === "draft" ? latest : approved) ?? versionThumb(latest),
    olderShots: n(o.olderShots),
  };
}

const normPlace = (v: unknown): Place => {
  const o = isObj(v) ? v : {};
  return { id: s(o.id), name: s(o.name), description: s(o.description), assetId: sOrNull(o.assetId) };
};

export function normDetail(v: unknown): CollectionDetail {
  const o = isObj(v) ? v : {};
  const collection = normCollectionCard(o.collection);
  const videos = arr(o.videos).map(normVideoCard);
  const characters = arr(o.characters).map(normCast);
  const c = isObj(o.counts) ? o.counts : {};
  const loc = isObj(o.locations) ? o.locations : {};
  return {
    collection: { ...collection, videoCount: collection.videoCount || videos.length, needs: collection.needs.review || collection.needs.sheets || collection.needs.olderLooks ? collection.needs : normNeeds(c) },
    settings: normResolved(o.settings),
    videos,
    characters,
    locations: { locations: arr(loc.locations).map(normPlace), props: arr(loc.props).map(normPlace) },
    counts: { videos: n(c.videos) || videos.length, characters: n(c.characters) || characters.length, ...normNeeds(c) },
    nextStep: normNextStep(o.nextStep),
  };
}

export function normVideoSettings(v: unknown): VideoSettings {
  const o = isObj(v) ? v : {};
  const kind = (COLLECTION_KINDS as readonly string[]).includes(s(o.kind)) ? (s(o.kind) as CollectionKind) : null;
  const group = (SETTINGS_GROUPS as readonly string[]).includes(s(o.group)) ? (s(o.group) as SettingsGroup) : null;
  return {
    kind,
    group,
    collectionId: sOrNull(o.collectionId),
    settings: normResolved(o.settings ?? o.values),
    pending: arr(o.pending)
      .filter(isObj)
      .map((p) => ({ key: s(p.key), from: SOURCES.includes(p.from as SettingSource) ? (p.from as SettingSource) : null, text: s(p.text) }))
      .filter((p) => p.key && p.text),
  };
}

export function normClip(v: unknown): Clip {
  const o = isObj(v) && isObj(v.item) ? v.item : isObj(v) ? v : {};
  const meta = isObj(o.meta) ? o.meta : {};
  const d = n(meta.durationS ?? meta.seconds);
  return { id: s(o.id), name: s(o.name) || "Clip", url: sOrNull(o.url), thumbUrl: sOrNull(o.thumbUrl ?? o.posterUrl), role: sOrNull(o.role ?? meta.role), durationS: d || null };
}

// ------------------------------------------------------------------ the source (http, or the mock)

export type CollectionsSource = {
  /** GET /channels (with collectionCount, videoCount) */
  channels(): Promise<ChannelCard[]>;
  tree(channelId: string): Promise<ChannelTree>;
  list(channelId?: string | null): Promise<CollectionCard[]>;
  detail(id: string): Promise<CollectionDetail>;
  create(b: { channelId: string; format: string; name: string; description?: string }): Promise<CollectionCard>;
  patch(id: string, b: CollectionPatch): Promise<CollectionCard>;
  archive(id: string): Promise<void>;
  /** GET /projects/:id/settings: provenance chips + pending "Use it?" */
  videoSettings(projectId: string): Promise<VideoSettings>;
  move(projectId: string, collectionId: string | null): Promise<void>;
  promote(projectId: string, name: string): Promise<CollectionCard>;
  adopt(projectId: string, keys: string[]): Promise<void>;
  /** GET /projects/:id/next, for videos on cards (P1) */
  next(projectId: string): Promise<NextStepData | null>;
  /** library videos that can be an intro or an outro */
  clips(): Promise<Clip[]>;
};

const enc = encodeURIComponent;
const listOf = (v: unknown, ...keys: string[]) => {
  if (Array.isArray(v)) return v;
  if (isObj(v)) for (const k of keys) if (Array.isArray(v[k])) return v[k] as unknown[];
  return [];
};

export const httpCollections: CollectionsSource = {
  async channels() {
    return listOf(await call("GET", "channels"), "channels", "items").map(normChannelCard);
  },
  async tree(channelId) {
    return normTree(await call("GET", `channels/${enc(channelId)}/tree`));
  },
  async list(channelId) {
    return listOf(await call("GET", `collections${channelId ? `?channelId=${enc(channelId)}` : ""}`), "items", "collections").map(normCollectionCard);
  },
  async detail(id) {
    return normDetail(await call("GET", `collections/${enc(id)}`));
  },
  async create(b) {
    return normCollectionCard(await call("POST", "collections", b));
  },
  async patch(id, b) {
    return normCollectionCard(await call("PATCH", `collections/${enc(id)}`, b));
  },
  async archive(id) {
    await call("POST", `collections/${enc(id)}/archive`, {});
  },
  async videoSettings(projectId) {
    return normVideoSettings(await call("GET", `projects/${enc(projectId)}/settings`));
  },
  async move(projectId, collectionId) {
    await call("POST", `projects/${enc(projectId)}/move`, { collectionId });
  },
  async promote(projectId, name) {
    const r = await call("POST", `projects/${enc(projectId)}/promote`, { name });
    return normCollectionCard(isObj(r) ? r.collection : r);
  },
  async adopt(projectId, keys) {
    await call("POST", `projects/${enc(projectId)}/adopt`, { keys });
  },
  async next(projectId) {
    return normNextStep(await call("GET", `projects/${enc(projectId)}/next`));
  },
  async clips() {
    return listOf(await call("GET", "library?kind=video"), "assets", "library", "items").map(normClip);
  },
};

let source: CollectionsSource = httpCollections;
export function installCollections(src: CollectionsSource) {
  source = src;
}
/** Always delegates to the installed source (http, or the mock under VITE_MOCK=1). */
export const collections: CollectionsSource = new Proxy({} as CollectionsSource, {
  get: (_t, k: keyof CollectionsSource) => {
    const v = source[k];
    return typeof v === "function" ? (v as (...a: unknown[]) => unknown).bind(source) : v;
  },
});

/** A failed tap, in plain words ("Couldn't move it. That video is being made right now."). */
export const toastError = (what: string, err: unknown) => toast(`${what} ${(err as Error)?.message ?? ""}`.trim(), "bad");

/** After a change to collections or where a video lives: the tree, the rail, the collection screens reload. */
export const collectionsChanged = () => bump("collections", "projects");

// ------------------------------------------------------------------ the current channel (remembered per browser)

const CURRENT_KEY = "cao.channel";
export type KeyStore = Pick<Storage, "getItem" | "setItem">;
function storage(st?: KeyStore | null): KeyStore | null {
  if (st !== undefined) return st;
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
export function readCurrentChannel(st?: KeyStore | null): string | null {
  try {
    return storage(st)?.getItem(CURRENT_KEY) || null;
  } catch {
    return null;
  }
}
export function writeCurrentChannel(id: string, st?: KeyStore | null): void {
  try {
    storage(st)?.setItem(CURRENT_KEY, id);
  } catch {
    // blocked storage: the switcher still works for this visit
  }
}

/** The channel to show: the one named (a /c/:id route), else the remembered one, else the first (the main channel). */
export function pickChannel<C extends { id: string }>(channels: C[], wanted?: string | null, remembered?: string | null): C | null {
  return channels.find((c) => c.id === wanted) ?? channels.find((c) => c.id === remembered) ?? channels[0] ?? null;
}

// ------------------------------------------------------------------ settings rows ("from the series" chips)

/** The chip beside a resolved value: where it came from, in the collection's own word. */
export function fromChip(from: SettingSource, kind: CollectionKind | null | undefined): string {
  if (from === "collection") return `From the ${kindNoun(kind)}`;
  if (from === "channel") return "From the channel";
  if (from === "video") return "This video";
  return "Default";
}

export const SETTING_LABEL: Record<string, string> = {
  lookId: "Look",
  imageModel: "Picture model",
  narrator: "Narrator",
  aspect: "Shape",
  themeMusicAssetId: "Theme music",
  locations: "Locations",
  props: "Props",
  productPhotoIds: "Product photos",
  heroPhotoId: "Hero photo",
  logoAssetId: "Logo",
  colors: "Colours",
  approvedClaims: "Approved claims",
  cta: "Call to action",
  songAssetId: "Song",
  lyrics: "Lyrics",
  sources: "Sources",
  introStyle: "Intro",
  introAssetId: "Intro clip",
  captionStyle: "Captions",
  hookFormat: "Hook",
  photoPoolIds: "Photos",
  musicAssetId: "Music",
  notation: "Notation",
  boardLook: "Board look",
};
/** The order rows appear in (the rest follow in their own order). */
const ROW_ORDER = ["lookId", "aspect", "narrator", "imageModel", "themeMusicAssetId", "songAssetId", "musicAssetId", "captionStyle", "hookFormat", "introStyle", "introAssetId", "colors", "logoAssetId", "cta"];

const WORDS: Record<string, string> = { title: "Title card", "cold-open": "Cold open", asset: "A clip", karaoke: "Karaoke", bold: "Bold", clean: "Clean", latex: "Maths notation", plain: "Plain text" };
const plural = (k: number, w: string) => `${k} ${w}${k === 1 ? "" : "s"}`;
const clip = (t: string, max = 48) => (t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t);

/** A setting's value in a few words ("Storybook", "Eric", "Wide 16:9", "3 photos"). */
export function settingText(key: string, value: unknown, ctx: { looks?: { id: string; name: string }[]; models?: Record<string, string> } = {}): string {
  if (value == null || value === "") return "None";
  if (key === "lookId" || key === "boardLook") return ctx.looks?.find((l) => l.id === value)?.name ?? s(value);
  if (key === "aspect") return [ASPECT_WORD[s(value) as keyof typeof ASPECT_WORD], s(value)].filter(Boolean).join(" ");
  if (key === "imageModel") return ctx.models?.[s(value)] ?? s(value).split(/[:/]/).pop()!.replace(/[-_]/g, " ");
  if (key === "narrator") return isObj(value) ? s(value.name) || "Chosen" : "Chosen";
  if (key === "colors") return isObj(value) ? `Accent ${s(value.accent).toUpperCase()}` : "Set";
  if (key === "locations" || key === "props") {
    const names = arr(value).map((x) => (isObj(x) ? s(x.name) : "")).filter(Boolean);
    return names.length ? clip(names.join(", ")) : "None";
  }
  if (key === "productPhotoIds" || key === "photoPoolIds") return plural(arr(value).length, "photo");
  if (key === "approvedClaims") return plural(arr(value).length, "claim");
  if (key.endsWith("AssetId") || key === "heroPhotoId") return key.includes("usic") || key === "songAssetId" ? "A track from your library" : key === "introAssetId" ? "A clip from your library" : "A picture from your library";
  if (typeof value === "string") return WORDS[value] ?? clip(value.replace(/\s+/g, " "));
  return "Set";
}

export type SettingRow = { key: string; label: string; text: string; from: SettingSource; chip: string };
/** Every resolved value as a row: label, value in words, and its chip. */
export function settingRows(resolved: Record<string, Resolved>, kind: CollectionKind | null, ctx: Parameters<typeof settingText>[2] = {}): SettingRow[] {
  const keys = Object.keys(resolved).sort((a, b) => {
    const ia = ROW_ORDER.indexOf(a);
    const ib = ROW_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  return keys.map((key) => {
    const r = resolved[key]!;
    return { key, label: SETTING_LABEL[key] ?? key, text: settingText(key, r.value, ctx), from: r.from, chip: fromChip(r.from, kind) };
  });
}

/** "Series · Storybook · 16:9": the line under a collection's name. */
export function collectionLine(c: Pick<CollectionCard, "kind" | "group">, resolved: Record<string, Resolved>, looks?: { id: string; name: string }[]): string {
  const look = resolved.lookId ? settingText("lookId", resolved.lookId.value, { looks }) : "";
  const aspect = resolved.aspect ? s(resolved.aspect.value) : "";
  return [groupWord(c.kind, c.group), look, aspect].filter(Boolean).join(" · ");
}

/** What on a collection needs the user (review + sheets + older looks): the card's dot. */
export const needsYou = (x: Needs) => x.review + x.sheets + x.olderLooks;

// ------------------------------------------------------------------ the rolled-up next step (core nextStep, rollUp)

/** core NEXT_STEP_IDS order: lower = more urgent. */
const ORDER: NextStepId[] = ["fix_failed", "finish_brief", "write_script", "make_sheet", "approve_sheet", "review", "update_older_looks", "suno_waiting", "produce", "qc_redo", "render", "busy", "watch", "none"];
const QUIET: NextStepId[] = ["watch", "busy", "none"];
const LOWERABLE = new Set(["Finish", "Write", "Make", "Approve", "Update", "Upload", "Watch", "The", "Scene", "Waiting"]);
/** Lower-cases our own leading verbs after a "Title: " prefix; names keep their capital (core lcFirst). */
export const lcFirst = (t: string) => (t && LOWERABLE.has(t.split(/[\s']/, 1)[0]!) ? t[0]!.toLowerCase() + t.slice(1) : t);

/**
 * A channel's (or collection's) one step from its parts' steps: the most urgent one that asks something of the user.
 * `title` prefixes a video's own step ("Pip and the Dark: approve Pip's sheet"); a collection's step comes already
 * prefixed (from GET /collections/:id), so it has none.
 */
export function rollUpSteps(parts: { step: NextStepData | null; title?: string; projectId?: string }[], doneText: string): NextStepData {
  let best: { step: NextStepData; rank: number; title?: string; projectId?: string } | null = null;
  for (const p of parts) {
    if (!p.step || QUIET.includes(p.step.id)) continue;
    const rank = ORDER.indexOf(p.step.id);
    if (!best || rank < best.rank) best = { step: p.step, rank, title: p.title, projectId: p.projectId };
  }
  if (!best) return { id: "none", label: "", spoken: parts.length ? doneText : "There are no videos here yet.", action: { tool: "whats_happening", args: {} } };
  const { step, title } = best;
  const projectId = step.projectId ?? best.projectId ?? (typeof step.action.args.project_id === "string" ? step.action.args.project_id : undefined);
  if (!title) return { ...step, ...(projectId ? { projectId } : {}) };
  return {
    ...step,
    label: `${title}: ${lcFirst(step.label)}`,
    spoken: `In "${title}": ${step.spoken}`,
    ...(step.blocking ? { blocking: { ...step.blocking, text: `${title}: ${lcFirst(step.blocking.text)}` } } : {}),
    ...(projectId ? { projectId } : {}),
  };
}

/** Where a tap on a rolled-up step goes: the video, on the tab that holds the thing (P1's nextTap, by link). */
export function stepHref(step: NextStepData | null): string | null {
  if (!step || !step.label) return null;
  const pid = step.projectId ?? (typeof step.action.args.project_id === "string" ? step.action.args.project_id : null);
  if (!pid) return null;
  // v4 (V4-C4): a sheet waiting for an OK opens that character's page
  const who = step.action.args.character;
  if (step.id === "approve_sheet" && typeof who === "string" && who) return `/p/${encodeURIComponent(pid)}/cast/${encodeURIComponent(who)}`;
  const tab =
    step.id === "review" ? "review"
    : step.id === "make_sheet" || step.id === "approve_sheet" || step.id === "update_older_looks" ? "cast"
    : step.id === "finish_brief" || step.id === "suno_waiting" ? "brief"
    : null;
  return `/p/${encodeURIComponent(pid)}${tab ? `?tab=${tab}` : ""}`;
}

// ------------------------------------------------------------------ breadcrumbs and moves

export type Crumb = { label: string; to: string | null };
/** Channel › Collection › Video. The last crumb is where you are (no link). */
export function crumbs(at: { channel?: { id: string; name: string } | null; collection?: { id: string; name: string } | null; video?: { title: string } | null }): Crumb[] {
  const out: Crumb[] = [];
  if (at.channel) out.push({ label: at.channel.name || "Your channel", to: `/c/${encodeURIComponent(at.channel.id)}` });
  if (at.collection) out.push({ label: at.collection.name, to: `/k/${encodeURIComponent(at.collection.id)}` });
  if (at.video) out.push({ label: at.video.title, to: null });
  if (out.length && !at.video) out[out.length - 1] = { ...out[out.length - 1]!, to: null };
  return out;
}

/** Where a video can go: the channel's collections that take its format (not the one it's in), and the ones that don't. */
export function moveTargets<C extends Pick<CollectionCard, "id" | "group">>(format: string, all: C[], currentId: string | null): { fits: C[]; others: C[] } {
  const rest = all.filter((c) => c.id !== currentId);
  return { fits: rest.filter((c) => fitsGroup(format, c.group)), others: rest.filter((c) => !fitsGroup(format, c.group)) };
}

/** "Make this a series" / "Make this a show"... (null for kinetic and demo: they always stand alone). */
export function promoteLabel(format: string | null | undefined): string | null {
  const k = kindOf(format);
  return k ? `Make this a ${k.group === "faceless" ? "series" : kindNoun(k.kind)}` : null;
}

/** The looks a group's formats offer (for the collection's look picker and names). */
export function looksOfGroup(group: SettingsGroup, formats: FormatInfo[] | null): { id: string; name: string }[] {
  const seen = new Set<string>();
  const out: { id: string; name: string }[] = [];
  for (const f of formats ?? []) if (fitsGroup(f.id, group)) for (const l of f.looks) if (!seen.has(l.id) && seen.add(l.id)) out.push({ id: l.id, name: l.name });
  return out;
}

// ------------------------------------------------------------------ hooks (cached lists for the rail and crumbs)

const lists = new Map<string, Promise<CollectionCard[]>>();
let listTick = "";
/** The channel's collections, cached across screens until something changes them (bump("collections")). */
export function useChannelCollections(channelId: string | null | undefined): CollectionCard[] | null {
  const tick = useRefresh("collections");
  const [data, setData] = useState<CollectionCard[] | null>(null);
  useEffect(() => {
    if (!channelId) return setData(null);
    if (tick !== listTick) {
      lists.clear();
      listTick = tick;
    }
    let live = true;
    let p = lists.get(channelId);
    if (!p) {
      p = collections.list(channelId).catch(() => {
        lists.delete(channelId);
        return [] as CollectionCard[];
      });
      lists.set(channelId, p);
    }
    void p.then((d) => live && setData(d));
    return () => {
      live = false;
    };
  }, [channelId, tick]);
  return data;
}

/**
 * The channel the screens are about: the one in the path (/c/:id, remembered from then on), else the remembered one,
 * else the main channel. Null while the account has no channel.
 */
export function useCurrentChannel(): Channel | null {
  const { path } = useLocation();
  const channels = useStore((st) => st.me?.channels);
  const named = channelInPath(path);
  const ch = pickChannel(channels ?? [], named, readCurrentChannel());
  useEffect(() => {
    if (named && ch?.id === named) writeCurrentChannel(named);
  }, [named, ch?.id]);
  return ch;
}
